import {
  getFromLocalStorage,
  LOCAL_STORAGE_USER,
  USER_ID,
} from "utils/localStorage";

/**
 * Which shell index.html shows before the bundle runs, and which one React
 * shows until the router has something to render.
 *
 * - `dashboard`: a user id is stored locally, so the router will land on the
 *   `AuthLayout` dashboard (public pages like `/login` redirect there).
 * - `public`: no stored user, or a standalone flow that never renders the
 *   dashboard frame even when signed in.
 *
 * The inline boot script in index.html (scripts/bootShell/bootShellScript.ts)
 * applies the same rule before the bundle loads, built from these constants.
 */
export type BootShellKind = "dashboard" | "public";

/**
 * Attribute on `<html>` holding the {@link BootShellKind} picked at boot. Only
 * the pre-rendered markup reads it: it goes stale after client-side navigation.
 */
export const BOOT_SHELL_ATTRIBUTE = "data-boot-shell";

/**
 * Attribute on `<html>` set to `none` when the page has no project/service tab
 * row under the main nav (MainNav's "no context" pages).
 */
export const BOOT_SHELL_TABS_ATTRIBUTE = "data-boot-shell-tabs";

/**
 * Routes that render their own full-screen layout rather than `AuthLayout`,
 * even for signed-in users (see the route tree in src/router.tsx).
 * bootShellKind.test.ts checks this against every route in the router.
 */
export const STANDALONE_PATH =
  /^\/(resetPassword|verifyBilling|idp|idpError|404|unsupported-browser|oauth|logout|welcome|account-deletion-result)(\/|$)/;

/**
 * Dashboard pages where MainNav shows no tab row (`getNoContextPages` in
 * src/layouts/navigation/MainNav.tsx). bootShellKind.test.ts checks it against
 * that list.
 */
export const NO_TABS_PATH =
  /^\/account(\/|$)|^\/projects\/[^/]+\/(support\/email|data-view)(\/|$)/;

/** The shell for the current URL. */
export const getBootShellKind = (
  pathname = window.location.pathname,
): BootShellKind =>
  getFromLocalStorage(USER_ID, LOCAL_STORAGE_USER, "") &&
  !STANDALONE_PATH.test(pathname)
    ? "dashboard"
    : "public";
